import React, { useMemo } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Stack,
    Typography,
} from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'
import { useAuth } from '../../contexts/AuthContext'
import { getMerchantTaxRemittances } from '../../utils/merchantToolStorage'

const panelStyles = {
    borderRadius: 6,
    background: 'rgba(27,16,64,0.96)',
    border: '1px solid rgba(183,166,255,0.16)',
}

export default function GovernmentDashboard() {
    const { lang } = useLanguage()
    const { user } = useAuth()
    const isFr = lang === 'fr'
    const authority = user?.authority || ''
    const agencyName =
        user?.agencyName ||
        user?.name ||
        (isFr ? 'Agence fiscale' : 'Tax Authority')

    const allReceipts = useMemo(() => getMerchantTaxRemittances(), [])
    const receipts = useMemo(() => {
        if (!authority) {
            return allReceipts
        }
        return allReceipts.filter((item) => {
            const itemAuthority = item.authority || item.taxType || ''
            return itemAuthority === authority
        })
    }, [allReceipts, authority])

    const totalCollected = useMemo(
        () => receipts.reduce((sum, item) => sum + Number(item.amount || 0), 0),
        [receipts]
    )
    const todayCollected = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10)
        return receipts
            .filter((item) => String(item.createdAt || '').slice(0, 10) === today)
            .reduce((sum, item) => sum + Number(item.amount || 0), 0)
    }, [receipts])
    const countryCount = useMemo(
        () => new Set(receipts.map((item) => item.country).filter(Boolean)).size,
        [receipts]
    )
    const scanReadyCount = useMemo(() => receipts.length, [receipts])

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Card
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                        boxShadow: '0 28px 60px rgba(49,26,137,0.38)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={2}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)' }}>
                                {isFr ? 'Supervision de l autorite fiscale' : 'Tax authority oversight'}
                            </Typography>
                            <Typography
                                sx={{
                                    color: '#FFFFFF',
                                    fontWeight: 900,
                                    fontSize: { xs: 30, md: 42 },
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                {isFr ? 'Tableau des recettes fiscales' : 'Tax Collections Dashboard'}
                            </Typography>
                            <Typography sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: 20 }}>
                                {agencyName}
                            </Typography>
                            {authority ? (
                                <Typography sx={{ color: 'rgba(255,255,255,0.74)' }}>
                                    {authority}
                                </Typography>
                            ) : null}
                            <Typography sx={{ color: 'rgba(255,255,255,0.80)', maxWidth: 860 }}>
                                {isFr
                                    ? 'Suivez uniquement les recus emis pour votre autorite, gardez la preuve officielle visible et donnez aux agents de terrain une reference claire pour le controle.'
                                    : 'Track only the receipts issued for your authority, keep official proof visible, and give field officers a clear reference point for enforcement checks.'}
                            </Typography>
                            <Button
                                variant="contained"
                                sx={{
                                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                    bgcolor: '#FFFFFF',
                                    color: '#0B0620',
                                    fontWeight: 900,
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    px: 3,
                                    width: { xs: '100%', sm: 'auto' },
                                }}
                            >
                                {isFr ? 'Scanner un recu' : 'Scan Receipt'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            title={isFr ? 'Total collecte' : 'Collected Total'}
                            value={totalCollected.toFixed(2)}
                            subtitle={isFr ? 'Recus de cette autorite' : 'Receipts for this authority'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            title={isFr ? 'Aujourd hui' : 'Today'}
                            value={todayCollected.toFixed(2)}
                            subtitle={isFr ? 'Volume fiscal du jour' : 'Today tax volume'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            title={isFr ? 'Pret au scan' : 'Scan Ready'}
                            value={`${scanReadyCount}`}
                            subtitle={isFr ? 'Recus controlables' : 'Receipts ready for field checks'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SummaryCard
                            title={isFr ? 'Pays actifs' : 'Active Countries'}
                            value={`${countryCount}`}
                            subtitle={isFr ? 'Marches visibles' : 'Visible markets'}
                        />
                    </Grid>
                </Grid>

                <Card sx={panelStyles}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                            {isFr ? 'Recus en direct pour cette autorite' : 'Live Receipts For This Authority'}
                        </Typography>
                        {receipts.length === 0 ? (
                            <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                {isFr
                                    ? 'Aucun recu fiscal n a encore ete enregistre pour cette autorite.'
                                    : 'No tax receipt has been recorded yet for this authority.'}
                            </Typography>
                        ) : (
                            <Stack spacing={1.2}>
                                {receipts.slice(0, 12).map((item) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            p: 1.8,
                                            borderRadius: 4,
                                            bgcolor: 'rgba(91,46,255,0.10)',
                                            border: '1px solid rgba(183,166,255,0.16)',
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            justifyContent="space-between"
                                            spacing={1}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 800 }}>
                                                    {item.receiptNumber}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.4 }}>
                                                    {(item.authority || item.taxType || authority)} • {item.country}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.4 }}>
                                                    {(item.reference || '').trim() || (isFr ? 'Sans reference' : 'No reference')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                                <Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>
                                                    {Number(item.amount || 0).toFixed(2)}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.4 }}>
                                                    {new Date(item.createdAt).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Stack>
        </Container>
    )
}

function SummaryCard({ title, value, subtitle }) {
    return (
        <Card sx={panelStyles}>
            <CardContent sx={{ p: 2.2 }}>
                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{title}</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 28, mt: 0.8 }}>{value}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.75 }}>
                    {subtitle}
                </Typography>
            </CardContent>
        </Card>
    )
}
